import { calculationPersistFingerprint } from "../utils/calculation-persist-fingerprint";
import { loadRecordSnapshot } from "../../../../collaboration/loadRecordSnapshot";
import { adoptCalculationSnapshot } from "../../../../slices/offer-calculation";
import { RecordCollaborationPanel } from "../../../../collaboration/RecordCollaborationPanel";
import { normalizeError } from "../../../../operationFeedback";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { Grid } from "@mui/material";
import { Cog6ToothIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FormikSaveBar } from "../../../utils/FormikSaveBar";
import { PMField } from "../../../utils/PMField";
import { useDispatch, useSelector } from "react-redux";
import PMSwitch from "../../../utils/PMSwitch";
import {
  createOfferCalculationAsync,
  updateOfferCalculationAsync,
  getOfferCalculationAsync,
} from "../../../../slices/offer-calculation";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import OfferCalculationWorkspace from "./calculation-workspace";
// Shared with Edit calc dialog — see module-builder-surface + offer-module-builder-sync rule.
import OfferCalculationModuleBuilder, {
  OfferCalculationUserInsertView,
} from "./module-builder-surface";
import OfferCalculationImport from "./calculation-import";
import CalculationVersionHistoryDialog from "./calculation-version-history";
import useStandaloneCalculationTemplateLoad from "../hooks/useStandaloneCalculationTemplateLoad";
import { PMDialog } from "../../../utils/PMDialog";
import {
  isOfferCalculationTemplateVersionsEnabled,
  isOfferCalculationV2Enabled,
  resolveOfferCalculationFeatureFlags,
} from "../utils";

/** Above module-map chrome / default PMDialog; matches module editor dialogs. */
const INSERT_VIEW_DIALOG_Z_INDEX = 4000;

const CALCULATION_TYPES = [
  { value: "standard", label: "Standard" },
  { value: "product", label: "Produkt" },
  { value: "hours", label: "Timer" },
  { value: "car_allowance", label: "Bilgodtgjørelse" },
  { value: "nested", label: "Nestet kalkyle" },
];

const CALCULATION_MODES = {
  lines: "lines",
  modules: "modules",
};

const CalculationSettingsDrawer = ({
  open,
  onClose,
  formik,
  calculationMode,
  canChangeMode,
  setCalculationMode,
  featureFlags,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="offer-calc-settings-scope">
      <div className="offer-calc-settings-scrim" onClick={onClose} aria-hidden />
      <aside
        aria-labelledby="offer-calc-settings-title"
        aria-modal="true"
        className="offer-calc-settings-drawer"
        role="dialog"
      >
        <div className="offer-calc-settings-drawer__head">
          <div className="offer-calc-settings-drawer__icon" aria-hidden>
            <Cog6ToothIcon />
          </div>
          <div>
            <h2 id="offer-calc-settings-title">{t("offer.calculation.settings.title")}</h2>
            <p>{t("offer.calculation.settings.subtitle")}</p>
          </div>
          <button
            aria-label={t("Close")}
            className="offer-calc-settings-drawer__close"
            onClick={onClose}
            type="button"
          >
            <XMarkIcon />
          </button>
        </div>

        <div className="offer-calc-settings-drawer__body">
          <section className="offer-calc-settings-drawer__section">
            <div className="offer-calculation-mode-picker">
              <div>
                <h4>{t("offer.calculation.mode.title")}</h4>
                <p>{t("offer.calculation.mode.subtitle")}</p>
              </div>
              <div className="offer-calculation-mode-picker__options">
                <button
                  className={[
                    "offer-calculation-mode-picker__option",
                    calculationMode === CALCULATION_MODES.lines ? "is-active" : "",
                  ].join(" ")}
                  disabled={!canChangeMode}
                  type="button"
                  onClick={() => setCalculationMode(CALCULATION_MODES.lines)}
                >
                  <strong>{t("offer.calculation.mode.lines_title")}</strong>
                  <span>{t("offer.calculation.mode.lines_hint")}</span>
                </button>
                <button
                  className={[
                    "offer-calculation-mode-picker__option",
                    calculationMode === CALCULATION_MODES.modules ? "is-active" : "",
                  ].join(" ")}
                  disabled={!canChangeMode}
                  type="button"
                  onClick={() => setCalculationMode(CALCULATION_MODES.modules)}
                >
                  <strong>{t("offer.calculation.mode.modules_title")}</strong>
                  <span>{t("offer.calculation.mode.modules_hint")}</span>
                </button>
              </div>
              {!canChangeMode ? (
                <p className="offer-calculation-mode-picker__locked">
                  {t("offer.calculation.mode.locked_hint")}
                </p>
              ) : null}
            </div>
          </section>

          <section className="offer-calc-settings-drawer__section">
            <PMField name="title" type="text" label={t("Tittel")} required {...formik} />
            {calculationMode === CALCULATION_MODES.lines ? (
              <PMField
                name="calculation_type"
                type="select"
                label={t("Type")}
                options={CALCULATION_TYPES}
                {...formik}
              />
            ) : null}
            <PMSwitch label={t("Aktiv")} name="active" {...formik} />
            <PMField
              name="description"
              type="text"
              label={t("Beskrivelse")}
              multiline
              rows={3}
              {...formik}
            />
          </section>

          {formik.values.id ? (
            <section className="offer-calc-settings-drawer__section">
              <OfferCalculationImport
                calculationId={formik.values.id}
                featureFlags={featureFlags}
              />
            </section>
          ) : null}
        </div>

        <div className="offer-calc-settings-drawer__foot">
          {formik.dirty ? (
            <span className="offer-calc-settings-drawer__dirty">
              <i />
              {t("offer.calculation.settings.unsaved")}
            </span>
          ) : (
            <span className="offer-calc-settings-drawer__hint">
              {t("offer.calculation.settings.save_hint")}
            </span>
          )}
          <button
            className="offer-calc-settings-drawer__done"
            onClick={onClose}
            type="button"
          >
            {t("offer.calculation.settings.done")}
          </button>
        </div>
      </aside>
    </div>,
    document.body
  );
};

export const OfferCalculationInfo = (props) => {
  const { t } = useTranslation();
  const { calculation, isLoading } = props;
  const dispatch = useDispatch();
  const [gridEditorOpen, setGridEditorOpen] = useState(false);
  const [remoteGridEpoch, setRemoteGridEpoch] = useState(0);
  const [activeModuleTab, setActiveModuleTab] = useState("modules");
  const [insertViewOpen, setInsertViewOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [internalSettingsOpen, setInternalSettingsOpen] = useState(false);
  const settingsOpen = props.settingsOpen ?? internalSettingsOpen;
  const setSettingsOpen = props.onSettingsOpenChange || setInternalSettingsOpen;
  const closeSettings = useCallback(() => setSettingsOpen(false), [setSettingsOpen]);

  const offerFeatureFlags = useSelector(
    (store) => store.offers?.offerFeatureFlags || {}
  );
  const offer = useSelector((store) => store.offers?.offer || {});
  const featureFlags =
    props.featureFlags ||
    resolveOfferCalculationFeatureFlags({
      calculation,
      offer,
      offerFeatureFlags,
    });
  const calculationV2Enabled =
    props.calculationV2Enabled ?? isOfferCalculationV2Enabled(featureFlags);

  // Block Formik enableReinitialize while a line↔group placement edit is
  // in flight — otherwise Redux `calculation` refresh snaps the grid back.
  const [placementLock, setPlacementLock] = useState(false);
  const placementLockRef = useRef(false);
  const lockPlacement = useCallback(() => {
    placementLockRef.current = true;
    setPlacementLock(true);
  }, []);
  const unlockPlacement = useCallback(() => {
    placementLockRef.current = false;
    setPlacementLock(false);
  }, []);

  const formikRef = useRef(null);
  const hasDraft = () => {
    const form = formikRef.current;
    return !!form && calculationPersistFingerprint(form.values) !== calculationPersistFingerprint(form.initialValues);
  };
  const {
    handleLoadPageTemplate,
    handleInsertTemplateAsLine,
    handleAttachNestedTemplate,
    handleNestedModulePersist,
    setNestedEditorId,
    setNestedEditorFocusRecommended,
    templateDialogs,
    collaborationBlocked,
  } = useStandaloneCalculationTemplateLoad({
    formikRef,
    lockPlacement,
    unlockPlacement,
  });

  const updateOrCreateCalculation = (values) => {
    if (values.id) {
      return dispatch(updateOfferCalculationAsync(values));
    } else {
      return dispatch(createOfferCalculationAsync(values));
    }
  };

  // New calculations are created via CalculationCreateDialog before this view mounts.
  if (!calculation?.id) {
    if (isLoading) {
      return <ResourceLoading />;
    }
    return null;
  }

  if (isLoading && calculation?.id) {
    return <ResourceLoading />;
  }

  return (
    <>
      <Formik
        initialValues={{
          id: calculation?.id,
          lock_version: calculation?.lock_version,
          updated_at: calculation?.updated_at,
          offer_id: calculation?.offer_id ?? null,
          original_calculation_id: calculation?.original_calculation_id ?? null,
          title: calculation?.title || "",
          description: calculation?.description || "",
          calculation_type: calculation?.calculation_type || "standard",
          active: calculation?.active !== undefined ? calculation?.active : true,
          items: calculation?.items || [],
          nested_calculations: calculation?.nested_calculations || [],
          groups: calculation?.groups || [],
          show_hours: calculation?.show_hours !== undefined ? calculation?.show_hours : true,
          show_quantities: calculation?.show_quantities !== undefined ? calculation?.show_quantities : true,
          show_products: calculation?.show_products !== undefined ? calculation?.show_products : true,
          show_setup_only: calculation?.show_setup_only ?? false,
          show_quantities_for_offer_item_only: calculation?.show_quantities_for_offer_item_only ?? false,
          include_product_short_description: calculation?.include_product_short_description ?? false,
          module_enabled: calculation?.module_enabled || false,
          module_graph: calculation?.module_graph || null,
          module_result: calculation?.module_result || null,
        }}
        enableReinitialize={!placementLock && !formikRef.current?.dirty}
        validationSchema={Yup.object({
          title: Yup.string().required("Tittel er påkrevd"),
        })}
        onSubmit={async (values, helpers) => {
          try {
            const result = await updateOrCreateCalculation(values);
            if (result.meta?.requestStatus === "rejected") throw result.payload || result.error;
            const saved = result.payload;
            if (!saved?.id) throw new Error(t("Noe gikk galt. Kunne ikke lagre data!"));
            helpers.resetForm({ values: { ...values, ...saved } });
            unlockPlacement();
            toast.success(t("Data ble lagret!"));
          } catch (error) {
            helpers.setStatus({ problem: normalizeError(error, { operationKey: "update_offer_calculation" }) });
          } finally {
            helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => {
          formikRef.current = formik;
          const calculationMode = formik.values.module_enabled ? CALCULATION_MODES.modules : CALCULATION_MODES.lines;
          const canChangeMode = !formik.values.id;

          const setCalculationMode = (mode) => {
            if (!canChangeMode) return;
            const useModuleBuilder = mode === CALCULATION_MODES.modules;
            formik.setFieldValue("module_enabled", useModuleBuilder);
            if (!useModuleBuilder) {
              formik.setFieldValue("module_graph", null);
              formik.setFieldValue("module_result", null);
            }
          };

          return (
          <Form>
            <RecordCollaborationPanel record={formik.values} recordType="calculation" section="items" editing={hasDraft()} aboveSaveBar={hasDraft()}
              saving={formik.isSubmitting || placementLock || collaborationBlocked || insertViewOpen || versionHistoryOpen || gridEditorOpen}
              canRefresh={() => !hasDraft() && !formikRef.current?.isSubmitting && !placementLockRef.current}
              onRefresh={async () => {
                const fresh = await loadRecordSnapshot("calculation", calculation.id);
                return { apply: () => { formik.resetForm({ values: fresh }); setRemoteGridEpoch(n => n + 1); dispatch(adoptCalculationSnapshot(fresh)); } };
              }}
              needsReview={Boolean(formik.status?.problem)} errorMessage={formik.status?.problem?.safeMessage} />
            <fieldset disabled={formik.isSubmitting} className="min-w-0">
            <Grid item xs={12} className="offer-calculation-builder-grid">
              <div className={calculationMode === CALCULATION_MODES.modules ? "offer-calculation-builder-shell" : "shadow sm:rounded-md"}>
                <div className={calculationMode === CALCULATION_MODES.modules ? "" : "space-y-6 bg-white py-6 px-4 sm:p-6"}>
                  {calculationMode === CALCULATION_MODES.lines ? (
                  <div className="offer-calculation-workspace-head">
                    <div>
                      <h3 className="text-lg font-medium leading-6 text-gray-900">
                        {t("offer.calculation.builder.section_title")}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {t("offer.calculation.builder.section_hint_lines")}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        className="offer-calculation-page__button"
                        onClick={handleLoadPageTemplate}
                        type="button"
                      >
                        {t("offer.calculation.workspace.load_page_template")}
                      </button>
                      <button
                        className="offer-calculation-page__button"
                        onClick={() => setSettingsOpen(true)}
                        type="button"
                      >
                        <Cog6ToothIcon className="h-4 w-4" />
                        {t("offer.calculation.settings.open")}
                      </button>
                    </div>
                  </div>
                  ) : activeModuleTab === "history" ? (
                    <div className="offer-calculation-workspace-tabs" role="tablist">
                      <button
                        type="button"
                        onClick={() => setActiveModuleTab("modules")}
                      >
                        {t("offer.calculation.builder.tab_modules")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setInsertViewOpen(true)}
                      >
                        {t("offer.calculation.builder.tab_insert")}
                      </button>
                      <button
                        className="is-active"
                        disabled={!formik.values.id || !isOfferCalculationTemplateVersionsEnabled(featureFlags)}
                        type="button"
                        onClick={() => {
                          if (!formik.values.id || !isOfferCalculationTemplateVersionsEnabled(featureFlags)) return;
                          setActiveModuleTab("history");
                        }}
                      >
                        {t("offer.calculation.builder.tab_history")}
                      </button>
                      <button
                        className="offer-calculation-workspace-tabs__settings"
                        onClick={() => setSettingsOpen(true)}
                        type="button"
                      >
                        <Cog6ToothIcon className="h-4 w-4" />
                        {t("offer.calculation.settings.open")}
                      </button>
                    </div>
                  ) : null}

                  <div className={calculationMode === CALCULATION_MODES.modules ? "offer-calculation-builder-body" : "pt-2"}>
                    {calculationMode === CALCULATION_MODES.lines ? (
                      <OfferCalculationWorkspace
                        onLocalEditorChange={setGridEditorOpen}
                        serverAdoptEpoch={remoteGridEpoch}
                        {...formik}
                        name="items"
                        calculationType={formik.values.calculation_type}
                        calculationId={formik.values.id}
                        calculation={calculation}
                        calculationV2Enabled={calculationV2Enabled}
                        authoritativeCalculation={calculation}
                        isLoading={isLoading}
                        offer={null}
                        groupsOwner={
                          formik.values?.id &&
                          (formik.values.offer_id == null ||
                            formik.values.offer_id === undefined)
                            ? {
                                ...formik.values,
                                kind: "calculation",
                                groups: formik.values.groups || calculation?.groups || [],
                              }
                            : offer?.id
                              ? { ...offer, kind: "offer" }
                              : null
                        }
                        onPlacementDirty={lockPlacement}
                        onItemsPatched={(items) => {
                          lockPlacement();
                          formik.setFieldValue("items", items);
                        }}
                        onUseTemplate={handleInsertTemplateAsLine}
                        onLoadPageTemplate={handleLoadPageTemplate}
                        onAttachNestedTemplate={handleAttachNestedTemplate}
                        onEditNestedCalculation={(nested, opts) => {
                          if (!nested?.id) return;
                          setNestedEditorFocusRecommended(
                            opts?.focusRecommended === true
                          );
                          setNestedEditorId(nested.id);
                        }}
                        onNestedModulePersist={handleNestedModulePersist}
                      />
                    ) : activeModuleTab === "history" ? (
                      <div className="offer-module-builder__card max-w-xl">
                        <div className="offer-module-builder__section-kicker">
                          {t("offer.calculation.builder.tab_history")}
                        </div>
                        <p className="offer-module-builder__hint">
                          {t("offer.calculation.builder.history_hint")}
                        </p>
                        <button
                          className="offer-module-builder__button is-primary mt-4"
                          type="button"
                          onClick={() => setVersionHistoryOpen(true)}
                        >
                          {t("offers.calculation.versions.title")}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex justify-end px-1">
                          <button
                            className="offer-calculation-page__button"
                            onClick={handleLoadPageTemplate}
                            type="button"
                          >
                            {t("offer.calculation.workspace.load_page_template")}
                          </button>
                        </div>
                        <OfferCalculationModuleBuilder
                          {...formik}
                          onOpenInsert={() => setInsertViewOpen(true)}
                          onOpenHistory={() => {
                            if (!formik.values.id || !isOfferCalculationTemplateVersionsEnabled(featureFlags)) return;
                            setActiveModuleTab("history");
                          }}
                          historyEnabled={Boolean(
                            formik.values.id && isOfferCalculationTemplateVersionsEnabled(featureFlags)
                          )}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Grid>

            </fieldset>
            <FormikSaveBar {...formik} isDirty={hasDraft()} disabled={formik.isSubmitting} />
            <PMDialog
              open={insertViewOpen}
              onClose={() => setInsertViewOpen(false)}
              size="6xl"
              title="offer.calculation.insert.title"
              closeOnOutsideClick
              zIndex={INSERT_VIEW_DIALOG_Z_INDEX}
            >
              <OfferCalculationUserInsertView {...formik} hideTitle />
            </PMDialog>
            <CalculationSettingsDrawer
              open={settingsOpen}
              onClose={closeSettings}
              formik={formik}
              calculationMode={calculationMode}
              canChangeMode={canChangeMode}
              setCalculationMode={setCalculationMode}
              featureFlags={featureFlags}
            />
            <CalculationVersionHistoryDialog
              open={versionHistoryOpen}
              onClose={() => setVersionHistoryOpen(false)}
              calculationId={formik.values.id}
              onRestored={() => {
                if (formik.values.id) dispatch(getOfferCalculationAsync(formik.values.id));
              }}
            />
            {templateDialogs}
          </Form>
          );
        }}
      </Formik>
    </>
  );
};

